import { FC, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useRidesNavigation } from "@ryde/redux";
import {
  capitalize,
  isNullOrEmptySpace,
  NO_VALUE_DISPLAY,
  RydeAppUnit,
  SINGLE_KEY,
  useHasPermissions,
  usePrefixedRouting,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { FramerTooltip } from "../../common";
import { useDecorateWithMark } from "../../utils";
import { IRenderColumnTemplate } from "./_core";

const TOOLTIP_MOUTH_LEAVE_DELAY = 300;

export const EntityIdLinkTemplate: FC<IRenderColumnTemplate> = ({
  id,
  name,
  value,
  rowOriginal,
  valueTranslated,
  formFieldTypeId,
  gridAttributes,
  e2eData,
}) => {
  const { decorateValue } = useDecorateWithMark();
  const { hasPermissions } = useHasPermissions({
    entity: gridAttributes?.relateToEntity as RydeAppUnit,
    action: "update",
  });
  const { t } = useTranslation();
  const { addPrefix } = usePrefixedRouting();
  const { getRideEditLink } = useRidesNavigation();
  const valueComputed = valueTranslated ?? value;
  const entityName = gridAttributes?.relateToEntity ?? "";
  const showId = gridAttributes?.showId;

  const highlightedValue = useMemo(() => {
    return decorateValue(valueComputed);
  }, [valueComputed, decorateValue]);

  const original = rowOriginal as any;
  const valueId = original[name] as number;

  const linkTo = useMemo(() => {
    if (!entityName || !value || !hasPermissions || !name || !rowOriginal) {
      return "";
    }

    switch (entityName) {
      case "ride":
        return getRideEditLink({
          id: valueId,
          keepQuery: true,
        });
      default:
        return addPrefix(`${entityName}s/edit/${valueId}`);
    }
  }, [
    entityName,
    value,
    hasPermissions,
    name,
    rowOriginal,
    getRideEditLink,
    valueId,
    addPrefix,
  ]);

  const tooltipContent = (): ReactNode => {
    if (!linkTo) {
      return "";
    }

    return (
      <span>
        {capitalize(t(`${entityName}.${SINGLE_KEY}`))} {": "}
        <Link
          to={linkTo}
          style={{
            color: rydeColors.dragBorderColor,
            textDecoration: "underline",
          }}
        >
          {showId && `${t("general.ID")}:${valueId} | `}
          {valueComputed}
        </Link>
      </span>
    );
  };

  return (
    <div
      className="cell_simple"
      data-e2e-id={`${name}_fieldid_${formFieldTypeId}`}
      data-e2e-value={value}
      data-e2e-template={"entity-id-link"}
      data-e2e-custom={e2eData}
      data-e2e-custom-field-id={id}
    >
      <FramerTooltip
        content={tooltipContent()}
        rtlStrippedBottomLeftPlacement
        mouseLeaveDelay={TOOLTIP_MOUTH_LEAVE_DELAY}
      >
        <span>
          {!isNullOrEmptySpace(valueComputed)
            ? highlightedValue
            : NO_VALUE_DISPLAY}
        </span>
      </FramerTooltip>
    </div>
  );
};
